import { useCallback, useEffect, useMemo, useState } from "react";

import { useGetUserAssociatedBUList } from "components/DataWrapper/BusinessUnit/useGetUserAssociatedBUList";
import { TInputTag } from "components/InputMultipleTag";

import { TGetCorpGlobalFilter } from "pages/Search/Corporation/Model/types";

interface Params {
  filter?: Partial<TGetCorpGlobalFilter>;
  onFilter?: (filter: TGetCorpGlobalFilter) => void;
}

const useCorpGlobalFilter = ({ filter, onFilter }: Params) => {
  const [businessUnitTags, setBusinessUnitTags] = useState<TInputTag[]>([]);
  const [openFilter, setOpenFilter] = useState(false);

  const { buList } = useGetUserAssociatedBUList({});

  const bupOptions: TInputTag[] = useMemo(() => {
    const arr = Object.values(buList);
    if (!arr) return [];
    return (
      arr?.map((bu) => ({
        name: bu.name,
        id: bu.idUnique,
        image: bu.profilePicture,
      })) || []
    );
  }, [buList]);

  const isEmptyFilter = useMemo(() => {
    const existingFilter: Record<string, string | string[] | undefined> = {
      bu: filter?.bu,
    };

    for (const key in existingFilter) {
      if (existingFilter?.[key]) return false;
    }

    return true;
  }, [filter]);

  const handleOpenFilter = () => {
    setOpenFilter((prev) => !prev);
  };

  const handleCloseFilter = () => {
    setOpenFilter(false);
  };

  const handleSubmitFilter = () => {
    onFilter?.({
      bu: businessUnitTags?.map((c) => c.id)?.join(","),
    });
    setOpenFilter(false);
  };

  const handleResetFilter = useCallback(() => {
    setBusinessUnitTags([]);
    onFilter?.({
      bu: undefined,
    });
  }, [onFilter]);

  const handleCloseAndResetFilter = useCallback(() => {
    handleCloseFilter();
    handleResetFilter();
  }, [handleResetFilter]);

  useEffect(() => {
    setBusinessUnitTags(constructFilterFromParam(bupOptions, filter?.bu));
  }, [bupOptions, filter]);

  return {
    openFilter,
    isEmptyFilter,
    businessUnitTags,
    bupOptions,
    setBusinessUnitTags,
    onOpenFilter: handleOpenFilter,
    onCloseFilter: handleCloseFilter,
    onSubmitFilter: handleSubmitFilter,
    onResetFilter: handleResetFilter,
    onCloseAndResetFilter: handleCloseAndResetFilter,
  };
};

export default useCorpGlobalFilter;

export const constructFilterFromParam = (
  tags: TInputTag[],
  filterParams?: string
) => {
  if (!filterParams || !tags?.length) return [];
  const params = filterParams.split(",");
  const filteredTags = tags.filter((tag) => params.includes(tag.id));

  return filteredTags.map((tag) => ({
    id: tag.id,
    name: tag.name,
  }));
};
